import AlertModal from "@src/straps/derived/AlertModal/AlertModal";
import { noop } from "lodash";
import React, { useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";

/**
 * Uses three ways of prompting for unsaved changes
 * 1. Browser beforeunload shown when closing the tab or changing url in browser
 * 2. Window confirmation alert shown when navigating via React Router
 * 3. Custom Modal shown when calling `confirmDiscardUnsavedChanges`
 */
export const useUnsavedChangesWarning = ({
  when,
  onDiscardChanges,
  message = "Are you sure you want to exit? Any new changes will be lost.",
  routeChangeWarningEnabled = true,
  ignoreParams,
}: {
  when: boolean;
  onDiscardChanges?: () => Promise<void> | void;
  message?: string;
  routeChangeWarningEnabled?: boolean;
  // prevent showing unsaved changes prompt when only these params change
  ignoreParams?: string[];
}) => {
  useRouteBlocker(when, message, routeChangeWarningEnabled, ignoreParams);
  React.useEffect(() => {
    window.onbeforeunload = when ? () => when : null;
    return () => {
      window.onbeforeunload = null;
    };
  }, [when]);

  const [showPrompt, setShowPrompt] = React.useState(false);
  const onCancel = React.useRef<() => void>(noop);
  const onConfirm = React.useRef<() => Promise<void> | void>(noop);

  const unsavedChangesPrompts = (
    <AlertModal
      variant="negative"
      open={showPrompt}
      title={message}
      onClose={onCancel.current}
      primaryButtonTitle="Yes"
      secondaryButtonTitle="Cancel"
      onPressPrimaryButton={onConfirm.current}
      onPressSecondaryButton={onCancel.current}
    />
  );
  // Resolves true when action has been approved, false when action needs to be cancelled
  const confirmDiscardUnsavedChanges = React.useCallback(
    () =>
      new Promise<boolean>((resolve) => {
        if (when) {
          onCancel.current = () => {
            setShowPrompt(false);
            resolve(false);
          };
          onConfirm.current = async () => {
            await onDiscardChanges?.();
            setShowPrompt(false);
            resolve(true);
          };
          setShowPrompt(true);
        } else {
          resolve(true);
        }
      }),
    [when, onDiscardChanges]
  );

  return { unsavedChangesPrompts, confirmDiscardUnsavedChanges };
};

// filters out ignored params and return a comparable string of all other params
const getFilteredSearchParams = (search: string, ignoreParams: string[]) => {
  const params = new URLSearchParams(search);
  ignoreParams.forEach((param) => params.delete(param));
  params.sort();
  return params.toString();
};

// notify user of unsaved changes when route changes
// uses history.block to allow navigation if only ignored params change
function useRouteBlocker(
  when: boolean,
  message: string,
  routeChangeWarningEnabled: boolean,
  ignoreParams?: string[]
) {
  const location = useLocation();
  const history = useHistory();

  useEffect(() => {
    // don't register the callback without unsaved changes
    if (when && routeChangeWarningEnabled) {
      const unblock = history.block((nextLocation) => {
        // If pathname changed, warn user
        if (location.pathname !== nextLocation.pathname) {
          return window.confirm(message) ? unblock() : false;
        }

        // Compare filtered query parameters
        const prevFilteredParams = getFilteredSearchParams(
          location.search,
          ignoreParams ?? []
        );
        const nextFilteredParams = getFilteredSearchParams(
          nextLocation.search,
          ignoreParams ?? []
        );

        if (prevFilteredParams !== nextFilteredParams) {
          return window.confirm(message) ? unblock() : false;
        }

        return unblock(); // Only ignored params changed, allow navigation
      });

      return () => {
        unblock();
      };
    }
  }, [
    history,
    when,
    message,
    ignoreParams,
    location,
    routeChangeWarningEnabled,
  ]);
}
